import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiLogger } from '@/utils/apiLogger';
import type { Database } from '@/integrations/supabase/types';

export type Script = Database['public']['Tables']['scripts']['Row'];
export type ScriptInsert = Database['public']['Tables']['scripts']['Insert'];
export type ScriptTemplate =
  Database['public']['Tables']['script_templates']['Row'];
export interface ScriptWithSections extends Script {
  script_sections: {
    template_id?: string | null;
    unresolved_content?: string;
    merge_values?: Record<string, string>;
    last_resolved_at?: string;
  } | null;
}

// Global cache for script data
let globalScriptsCache: ScriptWithSections[] | null = null;
let globalTemplatesCache: ScriptTemplate[] | null = null;
let lastFetchTime = 0;
let currentUserId: string | null = null;

// In-flight request tracking
const inFlightRequests = new Map<string, Promise<any>>();

// Cache duration (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

export function useOptimizedSupabaseScripts() {
  const [scripts, setScripts] = useState<ScriptWithSections[]>(
    globalScriptsCache || []
  );
  const [templates, setTemplates] = useState<ScriptTemplate[]>(
    globalTemplatesCache || []
  );
  const [loading, setLoading] = useState(false);
  const { user, session } = useAuth();
  const { toast } = useToast();
  const lastUserIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  // Get current user ID with optimization
  const getCurrentUserId = useCallback(() => {
    return user?.id || session?.user?.id || null;
  }, [user?.id, session?.user?.id]);

  // Deduplicated fetch function
  const fetchScripts = useCallback(
    async (forceRefresh = false) => {
      const userId = getCurrentUserId();
      if (!userId) {
        // console.log('No user ID available for scripts fetch');
        return;
      }

      // Check if we need to fetch (cache validity)
      const now = Date.now();
      const cacheValid = now - lastFetchTime < CACHE_DURATION;
      const userChanged = currentUserId !== userId;

      if (
        !forceRefresh &&
        cacheValid &&
        !userChanged &&
        globalScriptsCache &&
        globalTemplatesCache
      ) {
        // Use cached data
        if (isMountedRef.current) {
          setScripts(globalScriptsCache);
          setTemplates(globalTemplatesCache);
        }
        return;
      }

      // Check for in-flight requests
      const requestKey = `scripts-${userId}`;
      if (inFlightRequests.has(requestKey)) {
        // console.log('Request already in flight, waiting...');
        try {
          await inFlightRequests.get(requestKey);
          if (
            isMountedRef.current &&
            globalScriptsCache &&
            globalTemplatesCache
          ) {
            setScripts(globalScriptsCache);
            setTemplates(globalTemplatesCache);
          }
        } catch (error) {
          console.error('Error waiting for in-flight request:', error);
        }
        return;
      }

      // Create new request promise
      const requestPromise = (async () => {
        try {
          if (isMountedRef.current) {
            setLoading(true);
          }

          // console.log(`Fetching scripts for user: ${userId}`);

          // Log API requests
          apiLogger.log('scripts', 'SELECT', userId);
          apiLogger.log('script_templates', 'SELECT', userId);

          const [scriptsResponse, templatesResponse] = await Promise.all([
            supabase
              .from('scripts')
              .select(`*, script_sections`)
              .eq('user_id', userId)
              .order('created_at', { ascending: false }),

            supabase
              .from('script_templates')
              .select('*')
              .order('created_at', { ascending: false }),
          ]);

          if (scriptsResponse.error) throw scriptsResponse.error;
          if (templatesResponse.error) throw templatesResponse.error;

          // Update global cache
          globalScriptsCache = (scriptsResponse.data ||
            []) as ScriptWithSections[];
          globalTemplatesCache = templatesResponse.data || [];
          lastFetchTime = now;
          currentUserId = userId;

          // Update component state if still mounted
          if (isMountedRef.current) {
            setScripts(globalScriptsCache);
            setTemplates(globalTemplatesCache);
          }

          // console.log(`Scripts fetched successfully: ${globalScriptsCache.length} scripts, ${globalTemplatesCache.length} templates`);
        } catch (error) {
          // console.error('Error fetching scripts:', error);
          if (isMountedRef.current) {
            toast({
              title: 'Error',
              description: 'Failed to fetch scripts',
              variant: 'destructive',
            });
          }
          throw error;
        } finally {
          if (isMountedRef.current) {
            setLoading(false);
          }
        }
      })();

      // Store the request promise
      inFlightRequests.set(requestKey, requestPromise);

      try {
        await requestPromise;
      } finally {
        // Clean up the request promise
        inFlightRequests.delete(requestKey);
      }
    },
    [getCurrentUserId, toast]
  );

  const addScript = useCallback(
    async (
      scriptData: Omit<
        ScriptInsert,
        'id' | 'user_id' | 'created_at' | 'updated_at'
      >
    ) => {
      try {
        const userId = getCurrentUserId();
        if (!userId) throw new Error('User not authenticated');

        // Log API request
        apiLogger.log('scripts', 'INSERT', userId);

        const { data, error } = await supabase
          .from('scripts')
          .insert([{ ...scriptData, user_id: userId }])
          .select()
          .single();

        if (error) throw error;

        // Update global cache
        const newScriptFresh = {
          ...(data as ScriptWithSections),
          script_sections: data.script_sections
            ? { ...(data.script_sections as any) }
            : null,
        };

        if (globalScriptsCache) {
          globalScriptsCache = [newScriptFresh, ...globalScriptsCache];
        }

        if (isMountedRef.current) {
          setScripts((prev) => [newScriptFresh, ...prev]);
        }

        // console.log('Script added successfully:', data.name);
        return data;
      } catch (error) {
        // console.error('Error adding script:', error);
        toast({
          title: 'Error',
          description: `Failed to create script: ${error?.message || error}`,
          variant: 'destructive',
        });
        throw error;
      }
    },
    [toast]
  );

  const updateScript = useCallback(
    async (id: string, updates: Partial<Script>) => {
      try {
        const { data, error } = await supabase
          .from('scripts')
          .update(updates)
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;

        // ←←← THIS IS THE FIX
        const updatedScriptFresh = {
          ...(data as ScriptWithSections),
          script_sections: data.script_sections
            ? { ...(data.script_sections as any) }
            : null,
        };

        if (globalScriptsCache) {
          globalScriptsCache = globalScriptsCache.map((script) =>
            script.id === id ? updatedScriptFresh : script
          );
        }

        if (isMountedRef.current) {
          setScripts((prev) =>
            prev.map((script) =>
              script.id === id ? updatedScriptFresh : script
            )
          );
        }

        return updatedScriptFresh;
      } catch (error) {
        toast({
          title: 'Error',
          description: `Failed to update script: ${error?.message || error}`,
          variant: 'destructive',
        });
        throw error;
      }
    },
    [toast]
  );

  const deleteScript = useCallback(
    async (id: string) => {
      try {
        // Log API request
        apiLogger.log('scripts', 'DELETE', getCurrentUserId() || 'unknown');

        const { error } = await supabase.from('scripts').delete().eq('id', id);

        if (error) throw error;

        // Update global cache
        if (globalScriptsCache) {
          globalScriptsCache = globalScriptsCache.filter(
            (script) => script.id !== id
          );
        }

        // Update component state
        if (isMountedRef.current) {
          setScripts((prev) => prev.filter((script) => script.id !== id));
        }

        // console.log('Script deleted successfully');
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to delete script',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [toast]
  );

  const getIndustries = useCallback(() => {
    return Array.from(
      new Set(scripts.map((s) => s.target_industry).filter(Boolean))
    );
  }, [scripts]);

  const getOffers = useCallback(() => {
    return Array.from(new Set(scripts.map((s) => s.item_sold).filter(Boolean)));
  }, [scripts]);

  // Optimized effect - only trigger on actual user change
  useEffect(() => {
    const userId = getCurrentUserId();

    // Only fetch if user ID actually changed or if we don't have cached data for this user
    if (
      userId &&
      (userId !== lastUserIdRef.current || currentUserId !== userId)
    ) {
      lastUserIdRef.current = userId;
      fetchScripts();
    }
  }, [getCurrentUserId, fetchScripts]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    scripts,
    templates,
    loading,
    addScript,
    updateScript,
    deleteScript,
    refreshScripts: () => fetchScripts(true),
    getIndustries,
    getOffers,
  };
}

// Utility function to clear cache (useful for logout)
export function clearScriptsCache() {
  globalScriptsCache = null;
  globalTemplatesCache = null;
  lastFetchTime = 0;
  currentUserId = null;
  inFlightRequests.clear();
}
